import React, { useEffect, useState } from 'react';

function MenuSetup({ token }) {
  const emptyForm = { menu_item_code: '', menu_item_name: '', main_item_code: '', hsn_code: '', serving_ml: '30', per_unit_price: '', quantity_ml_per_unit: '', gst_id: '', tax_id: '', full_bottle_sale: false };
  const [items, setItems] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [taxRows, setTaxRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bulkTaxForm, setBulkTaxForm] = useState({ liquor_category_id: '', tax_id: '' });
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const propertyId = localStorage.getItem('selectedPropertyId') || '';
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-property-id': propertyId, ...extra });

  useEffect(() => {
    loadItems();
    loadItemMaster();
    loadTaxRows();
    loadCategories();
  }, [token]);

  useEffect(() => {
    if (!form.full_bottle_sale || !form.main_item_code) return;
    const selectedMainItem = masterItems.find(item => item.main_item_code === form.main_item_code);
    const bottleQty = Number(selectedMainItem?.quantity_ml_per_unit || 0);
    if (bottleQty <= 0) return;

    setForm(prev => ({
      ...prev,
      quantity_ml_per_unit: selectedMainItem.quantity_ml_per_unit,
      serving_ml: String(bottleQty)
    }));
  }, [form.full_bottle_sale, form.main_item_code, masterItems]);

  const loadItems = () => {
    fetch('/api/menu-setup', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setItems(data);
        } else if (data && Array.isArray(data.items)) {
          setItems(data.items);
        } else {
          setItems([]);
        }
      })
      .catch(() => {
        setError('Failed to load menu setup');
        setItems([]);
      });
  };

  const loadItemMaster = () => {
    fetch('/api/item-master', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setMasterItems(data);
        } else if (data && Array.isArray(data.items)) {
          setMasterItems(data.items);
        } else {
          setMasterItems([]);
        }
      })
      .catch(() => setMasterItems([]));
  };

  const loadTaxRows = () => {
    fetch('/api/tax-master', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setTaxRows(data);
        } else if (data && Array.isArray(data.items)) {
          setTaxRows(data.items);
        } else {
          setTaxRows([]);
        }
      })
      .catch(() => setTaxRows([]));
  };

  const loadCategories = () => {
    fetch('/api/liquor-category', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCategories(data);
        } else if (data && Array.isArray(data.items)) {
          setCategories(data.items);
        } else {
          setCategories([]);
        }
      })
      .catch(() => setCategories([]));
  };

  const handleChange = e => {
    if (e.target.name === 'full_bottle_sale') {
      const checked = e.target.checked;
      setForm(prev => ({
        ...prev,
        full_bottle_sale: checked,
      }));
      setError('');
      return;
    }

    if (e.target.name === 'main_item_code') {
      const selected = masterItems.find(item => item.main_item_code === e.target.value);
      const selectedBottleQty = Number(selected?.quantity_ml_per_unit || 0);
      setForm(prev => ({
        ...prev,
        main_item_code: e.target.value,
        hsn_code: selected?.hsn_code || '',
        quantity_ml_per_unit: selected?.quantity_ml_per_unit || '',
        serving_ml: prev.full_bottle_sale && selectedBottleQty > 0 ? String(selectedBottleQty) : prev.serving_ml
      }));
      setError('');
      return;
    }

    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreate = async () => {
    if (!form.menu_item_code || !form.menu_item_name || !form.main_item_code) {
      setError('MenuItemCode, Menu Item Name and Main Item Selection are required.');
      return;
    }
    const selectedTax = taxRows.find((tax) => String(tax.id) === String(form.tax_id));
    if (!form.full_bottle_sale && Number(form.quantity_ml_per_unit || 0) > 0 && Number(form.serving_ml || 0) > Number(form.quantity_ml_per_unit || 0)) {
      setError('Serving Qty cannot exceed Main Item Qty in ML per Bottle.');
      return;
    }

    try {
      const res = await fetch('/api/menu-setup', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...form,
          tax_regime: selectedTax ? String(selectedTax.tax_regime || 'NONE').toUpperCase() : 'NONE',
          tax_id: form.tax_id ? Number(form.tax_id) : null
        })
      });
      if (res.ok) {
        const created = await res.json();
        setItems([...items, created]);
        setForm(emptyForm);
        setError('');
      } else {
        const data = await res.json();
        setError(data.error || 'Create failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = item => {
    const selected = masterItems.find(master => master.main_item_code === item.main_item_code);
    setEditingId(item.id);
    setForm({
      ...emptyForm,
      ...item,
      serving_ml: item.serving_ml || '30',
      per_unit_price: item.per_unit_price || '',
      gst_id: item.gst_id ? String(item.gst_id) : '',
      tax_id: item.tax_id ? String(item.tax_id) : '',
      full_bottle_sale: Boolean(item.full_bottle_sale),
      quantity_ml_per_unit: selected?.quantity_ml_per_unit || '',
      hsn_code: selected?.hsn_code || item.hsn_code || ''
    });
  };

  const handleUpdate = async () => {
    if (!form.menu_item_code || !form.menu_item_name || !form.main_item_code) {
      setError('MenuItemCode, Menu Item Name and Main Item Selection are required.');
      return;
    }
    const selectedTax = taxRows.find((tax) => String(tax.id) === String(form.tax_id));
    if (!form.full_bottle_sale && Number(form.quantity_ml_per_unit || 0) > 0 && Number(form.serving_ml || 0) > Number(form.quantity_ml_per_unit || 0)) {
      setError('Serving Qty cannot exceed Main Item Qty in ML per Bottle.');
      return;
    }

    try {
      const res = await fetch(`/api/menu-setup/${editingId}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...form,
          tax_regime: selectedTax ? String(selectedTax.tax_regime || 'NONE').toUpperCase() : 'NONE',
          tax_id: form.tax_id ? Number(form.tax_id) : null
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setItems(items.map(i => i.id === editingId ? updated : i));
        setForm(emptyForm);
        setEditingId(null);
        setError('');
      } else {
        const data = await res.json();
        setError(data.error || 'Update failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleDelete = async id => {
    try {
      const res = await fetch(`/api/menu-setup/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (res.ok) {
        setItems(items.filter(i => i.id !== id));
        setError('');
      } else {
        setError('Delete failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const applyBulkTaxByCategory = async () => {
    if (!bulkTaxForm.liquor_category_id) {
      setError('Select Liquor Category for bulk tax assignment.');
      return;
    }
    const selectedTax = taxRows.find((tax) => String(tax.id) === String(bulkTaxForm.tax_id));
    try {
      const res = await fetch('/api/menu-setup/bulk-tax-by-category', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          liquor_category_id: Number(bulkTaxForm.liquor_category_id),
          tax_regime: selectedTax ? String(selectedTax.tax_regime || 'NONE').toUpperCase() : 'NONE',
          tax_id: bulkTaxForm.tax_id ? Number(bulkTaxForm.tax_id) : null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Bulk tax assignment failed');
        return;
      }
      setError('');
      loadItems();
    } catch {
      setError('Network error');
    }
  };

  return (
    <div className="page-shell">
      <h2 style={{color:'var(--theme-primary-dark)'}}>Menu Setup</h2>
      {error && <div className="error-banner">{error}</div>}
      <div className="filter-bar" style={{marginBottom:'2rem'}}>
        <h3>{editingId ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
        <div style={{marginBottom:'0.75rem',color:'var(--theme-primary-dark)',fontWeight:'bold'}}>Required Fields</div>
        <form style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:'1rem'}}>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Menu Item Code *</span>
            <input name="menu_item_code" placeholder="MenuItemCode" value={form.menu_item_code} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Menu Item Name *</span>
            <input name="menu_item_name" placeholder="Menu Item Name" value={form.menu_item_name} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Main Item *</span>
            <select name="main_item_code" value={form.main_item_code} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}}>
              <option value="">-- Select Main Item --</option>
              {masterItems.map(masterItem => (
                <option key={masterItem.main_item_code} value={masterItem.main_item_code}>
                  {masterItem.main_item_code} - {masterItem.main_item_name}
                </option>
              ))}
            </select>
          </label>
        </form>
        <div style={{marginTop:'1rem',marginBottom:'0.75rem',color:'var(--theme-primary-dark)',fontWeight:'bold'}}>Selling Details</div>
        <form style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))',gap:'1rem'}}>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Serving Qty (ML) per Unit</span>
            <input name="serving_ml" placeholder="Serving Qty in ML per unit" value={form.serving_ml} onChange={handleChange} disabled={form.full_bottle_sale} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1',background:form.full_bottle_sale ? '#ecf0f1' : '#fff'}} />
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Per Unit Price</span>
            <input name="per_unit_price" placeholder="Per Unit Price" value={form.per_unit_price} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}} />
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>HSN Code <span style={{fontWeight:400,color:'#7f8c8d',fontSize:'0.72rem'}}>(from Item Master)</span></span>
            <input name="hsn_code" placeholder="Auto-filled from Item Master" value={form.hsn_code} readOnly title="HSN Code is sourced from Item Master. Edit it there to change." style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1',background:'#ecf0f1',cursor:'not-allowed'}} />
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Tax Profile</span>
            <select name="tax_id" value={form.tax_id} onChange={handleChange} style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1',background:'#fff'}}>
              <option value="">No Tax</option>
              {taxRows.map((tax) => (
                <option key={tax.id} value={tax.id}>
                  {tax.tax_code} - {tax.tax_name} [{String(tax.tax_regime || 'NONE').toUpperCase()}] ({Number(tax.tax_percent || 0).toFixed(2)}%)
                </option>
              ))}
            </select>
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Main Item Qty (ML) per Bottle</span>
            <input name="quantity_ml_per_unit" placeholder="Main Item Qty in ML per Bottle" value={form.quantity_ml_per_unit} readOnly style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1',background:'#ecf0f1'}} />
          </label>
          <label style={{display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.5rem 0.25rem'}}>
            <input type="checkbox" name="full_bottle_sale" checked={form.full_bottle_sale} onChange={handleChange} />
            Full Bottle Sale (Serving = Main Item Qty)
          </label>
        </form>
        <div style={{marginTop:'1rem',display:'flex',gap:'1rem'}}>
          {editingId ? (
            <>
              <button type="button" onClick={handleUpdate} className="btn-success" style={{padding:'0.75rem 2rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>Update Item</button>
              <button type="button" onClick={handleCancel} className="btn-muted" style={{padding:'0.75rem 2rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>Cancel</button>
            </>
          ) : (
            <button type="button" onClick={handleCreate} className="btn-primary" style={{padding:'0.75rem 2rem',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'1rem'}}>Add Menu Item</button>
          )}
        </div>
      </div>
      <div style={{background:'#eef6ff',padding:'1rem',borderRadius:'8px',marginBottom:'2rem',border:'1px solid #d6e9ff'}}>
        <h3 style={{marginTop:0,color:'var(--theme-primary)'}}>Bulk Tax Assignment By Category</h3>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:'0.75rem'}}>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Liquor Category *</span>
            <select
              value={bulkTaxForm.liquor_category_id}
              onChange={(e) => setBulkTaxForm((prev) => ({ ...prev, liquor_category_id: e.target.value }))}
              style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1'}}
            >
              <option value="">-- Select Category --</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label style={{display:'grid',gap:'0.35rem'}}>
            <span style={{fontSize:'0.82rem',fontWeight:600,color:'#2f3640'}}>Tax Profile</span>
            <select
              value={bulkTaxForm.tax_id}
              onChange={(e) => setBulkTaxForm((prev) => ({ ...prev, tax_id: e.target.value }))}
              style={{padding:'0.75rem',borderRadius:'6px',border:'1px solid #dcdde1',background:'#fff'}}
            >
              <option value="">No Tax</option>
              {taxRows.map((tax) => (
                <option key={tax.id} value={tax.id}>
                  {tax.tax_code} - {tax.tax_name} [{String(tax.tax_regime || 'NONE').toUpperCase()}] ({Number(tax.tax_percent || 0).toFixed(2)}%)
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={{marginTop:'0.8rem'}}>
          <button
            type="button"
            onClick={applyBulkTaxByCategory}
            className="btn-primary"
            style={{padding:'0.7rem 1.2rem',border:'none',borderRadius:'6px',cursor:'pointer'}}
          >
            Apply Bulk Tax
          </button>
        </div>
      </div>
      <h3>Menu Items List</h3>
      {!items || items.length === 0 ? (
        <p>No menu items found</p>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse',background:'#fff'}}>
          <thead>
            <tr style={{background:'var(--theme-primary-dark)',color:'#fff'}}>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Menu Item Code</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Menu Item Name</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Main Item Code</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Serving Qty in ML per unit</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Full Bottle Sale</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Per Unit Price</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>HSN Code</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Tax Regime</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Tax Profile</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Tax %</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Main Item Qty in ML per Bottle</th>
              <th style={{padding:'0.75rem',textAlign:'left',borderBottom:'2px solid #dcdde1'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={{borderBottom:'1px solid #dcdde1'}}>
                <td style={{padding:'0.75rem'}}>{item.menu_item_code}</td>
                <td style={{padding:'0.75rem'}}>{item.menu_item_name}</td>
                <td style={{padding:'0.75rem'}}>{item.main_item_code}</td>
                <td style={{padding:'0.75rem'}}>{item.serving_ml || 30}</td>
                <td style={{padding:'0.75rem'}}>{item.full_bottle_sale ? 'Yes' : 'No'}</td>
                <td style={{padding:'0.75rem'}}>Rs. {item.per_unit_price || 0}</td>
                <td style={{padding:'0.75rem'}}>{item.hsn_code}</td>
                <td style={{padding:'0.75rem'}}>{item.tax_regime || 'NONE'}</td>
                <td style={{padding:'0.75rem'}}>{item.tax_name || '-'}</td>
                <td style={{padding:'0.75rem'}}>{Number(item.gst_percent || 0).toFixed(2)}%</td>
                <td style={{padding:'0.75rem'}}>{masterItems.find(master => master.main_item_code === item.main_item_code)?.quantity_ml_per_unit || 0}</td>
                <td style={{padding:'0.75rem',display:'flex',gap:'0.5rem'}}>
                  <button onClick={() => handleEdit(item)} style={{background:'#3498db',color:'#fff',border:'none',padding:'0.5rem 1rem',borderRadius:'4px',cursor:'pointer'}}>Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="btn-danger" style={{border:'none',padding:'0.5rem 1rem',borderRadius:'4px',cursor:'pointer'}}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MenuSetup;
